/* eslint-disable no-underscore-dangle */
/* eslint-disable max-len */
/* eslint-disable no-console */
/* eslint-disable no-constant-condition */
/* eslint-disable no-unused-vars */
import { Avatar, Button, Form, Input, message, Upload, Select } from "antd";
import type { RcFile } from "antd/es/upload/interface";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { copyObjects } from "../../util/copyObjects";
import Colors from "../../config/Colors";
import DaggerCard from "../Cards/DaggerCard/DaggerCard";
import { BaseUrl, StagingUrl, uploadUrl } from "@/config/urls";
import { CustomIcons } from "../../assets/index";

const MAX_LENGTH = 200;
const { Dragger } = Upload;

type NewCommunityFormProp = {
  communityValues?: ICommunity;
  onFormFinish: (emergency: IAddCommunity) => void;
  onModelClose: () => void;
  isLoading: boolean;
  isSuccess: boolean;
  onDelete?: () => void;
  isDeleting?: boolean;
};

const NewCommunityForm = ({
  communityValues,
  onFormFinish,
  onModelClose,
  isLoading,
  isSuccess,
  onDelete,
  isDeleting = false,
}: NewCommunityFormProp) => {
  const [form] = Form.useForm();
  const [charactersLeft, setCharactersLeft] = useState(MAX_LENGTH);
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState<boolean>(false);

  const onFinish = async (formValues: any) => {
    const values: IAddCommunity = formValues as IAddCommunity;
    onFormFinish(values);
  };

  const handleClose = () => {
    form.resetFields();
    setImageUrl("");
    setCharactersLeft(MAX_LENGTH);
    onModelClose();
  };

  // Manual upload function using axios
  const handleImageUpload = async (file: RcFile): Promise<string | false> => {
    // Validate file type
    const isJpegOrPngOrGif = ["image/jpeg", "image/png", "image/gif"].includes(
      file.type
    );
    if (!isJpegOrPngOrGif) {
      message.error("You can only upload JPG/PNG/GIF file!");
      return false;
    }

    // Validate file size (10MB)
    const isLt2M = file.size / 1024 / 1024 < 10;
    if (!isLt2M) {
      message.error("Image must smaller than 10MB!");
      return false;
    }

    // Create FormData
    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);

      // Make upload request
      const response = await axios.post(
        `${StagingUrl}/util/post/document`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "X-API-Version": "1.0",
            "X-API-Client": "SaferCity",
            "X-Platform": "Web",
            "X-Tenant": "SaferCity",
          },
        }
      );

      if (response.data?.path) {
        message.success("Image uploaded successfully!");
        return response.data.path;
      } else {
        message.error("Upload succeeded but response format is invalid");
        return false;
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || "Failed to upload image");
      return false;
    } finally {
      setUploading(false);
    }
  };

  // Handle before upload - prevent automatic upload
  const beforeUpload = (file: RcFile) => {
    handleImageUpload(file).then((path) => {
      if (path) {
        setImageUrl(path);
        form.setFieldsValue({ communityIcon: path });
      }
    });
    return false; // Prevent automatic upload
  };

  useEffect(() => {
    if (communityValues) {
      form.setFieldsValue(communityValues);
      if (communityValues.communityIcon) {
        setImageUrl(communityValues.communityIcon);
      }
      if (communityValues.communityDescription) {
        setCharactersLeft(
          MAX_LENGTH - communityValues.communityDescription.length
        );
      }
    } else {
      form.resetFields();
    }
  }, [communityValues, form]);

  useEffect(() => {
    if (isSuccess) {
      setCharactersLeft(MAX_LENGTH);
      form.resetFields();
    }
  }, [isSuccess]);

  return (
    <Form
      form={form}
      name="newCommunity"
      onFinish={onFinish}
      layout="vertical"
      style={{ width: "100%" }}
      className="grid"
      initialValues={communityValues}
    >
      <div className="col-12">
        <Form.Item
          name="communityName"
          label="Name of community"
          rules={[{ required: true, message: "Please enter community name!" }]}
          className="mb-1"
        >
          <Input size="large" placeholder="Sandton CPF" />
        </Form.Item>
      </div>
      <div className="col-12">
        <Form.Item
          name="communityDescription"
          label="Description"
          rules={[
            { required: true, message: "Please enter community description!" },
            {
              max: MAX_LENGTH,
              message: `Description cannot be longer than ${MAX_LENGTH} characters`,
            },
          ]}
          className="mb-1"
        >
          <>
            <Input.TextArea
              value={form.getFieldValue("communityDescription")}
              placeholder="This is the community of Sandton CPF"
              maxLength={MAX_LENGTH}
              onChange={(e) => {
                form.setFieldsValue({ communityDescription: e.target.value });
                setCharactersLeft(MAX_LENGTH - e.target.value.length);
              }}
              rows={3}
            />
            {charactersLeft <= 100 && (
              <div
                className="py-1"
                style={{
                  fontSize: "13px",
                  fontStyle: "normal",
                  fontWeight: "400",
                  color: Colors.subtleTextColor,
                }}
              >
                {charactersLeft} characters left
              </div>
            )}
          </>
        </Form.Item>
      </div>
      <div className="col-12">
        <Form.Item
          name={"whiteLabelId"}
          label="Select a white-label app"
          className="mb-1"
          required
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const whiteLabelId = getFieldValue("whiteLabelId");

                if (whiteLabelId && !value) {
                  return Promise.reject(
                    new Error("Please select a white-label app!")
                  );
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Select
            onChange={(value) => {
              const tenantMapping = {
                0: "SaferCity",
                1: "Curro",
              };
              form.setFieldsValue({
                TenantClientId:
                  tenantMapping[value as keyof typeof tenantMapping],
              });
            }}
            placeholder="White-label app"
            size="large"
            options={[
              { value: 0, label: "SaferCity" },
              { value: 1, label: "Curro" },
            ]}
          />
        </Form.Item>
      </div>
      <Form.Item
        name="communityIcon"
        label="Icon"
        rules={[
          { required: true, message: "Please enter community icon!" }, // Custom validator to check if a valid logo file has been uploaded
          () => ({
            validator() {
              // If the file is currently uploading, skip this validation check
              if (!imageUrl) {
                // Check if logo URL is not set
                return Promise.reject(new Error("Please upload a valid logo."));
              }
              return Promise.resolve();
            },
          }),
        ]}
        className="mb-5 col-12"
      >
        <div className="flex w-full gap-6 flx-row justify-content-start">
          {imageUrl.length > 0 ? (
            <Avatar
              shape="square"
              size={64}
              alt="emergency icon"
              src={imageUrl}
            />
          ) : (
            <Avatar shape="square" size={64} alt="emergency icon" />
          )}
          <Dragger
            name="file"
            multiple={false}
            beforeUpload={beforeUpload}
            className="w-full"
            style={{ background: "transparent" }}
            maxCount={1}
            showUploadList={false}
            accept=".jpg,.jpeg,.png,.gif"
            disabled={uploading}
          >
            <DaggerCard />
          </Dragger>
        </div>
      </Form.Item>
      <Form.Item className="w-12 pt-2 mb-1">
        <div className="flex w-full gap-2 col-12 justify-content-end align-items-center">
          <Button
            type="primary"
            ghost
            onClick={handleClose}
            size="large"
            className="w-3"
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={isLoading}
            size="large"
            className="w-3 bg-[#1757a2] shadow-none"
            disabled={uploading}
          >
            Save
          </Button>
        </div>
      </Form.Item>
    </Form>
  );
};

export default NewCommunityForm;
